import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './DashboardPage.css';

function PaymentsPage() {
    const navigate = useNavigate();
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [upcomingPayments, setUpcomingPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const API_URL = process.env.REACT_APP_API_URL || '';

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        if (session && session.userId) {
            // Fetch Payment History
            fetch(`${API_URL}/api/payments/${session.userId}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        const mappedHistory = data.payments.map(p => ({
                            id: p.id,
                            date: new Date(p.payment_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                            loanType: p.description || 'EMI Payment',
                            accountNumber: p.loan_id ? `LN00${p.loan_id}` : 'N/A',
                            amount: p.amount.toLocaleString('en-IN'),
                            status: p.status === 'Success' ? 'Paid' : p.status,
                            method: p.payment_method
                        }));
                        setPaymentHistory(mappedHistory);
                    }
                })
                .catch(err => console.error('Error fetching payments:', err));

            // Fetch Upcoming Payments (from loans table where status is Active)
            fetch(`${API_URL}/api/loans/${session.userId}`)
                .then(res => res.json())
                .then(data => {
                    if (data.success) {
                        const activeLoans = data.loans.filter(l => l.status === 'Active');
                        const mappedUpcoming = activeLoans.map(l => {
                            const dueDate = new Date(l.due_date);
                            const today = new Date();
                            const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

                            return {
                                id: l.id,
                                loanType: l.type,
                                accountNumber: l.account_number,
                                amount: l.emi.toLocaleString('en-IN'),
                                dueDate: dueDate.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }),
                                daysLeft: diffDays,
                                status: l.status // Use actual status
                            };
                        });
                        setUpcomingPayments(mappedUpcoming);
                    }
                })
                .catch(err => console.error('Error fetching upcoming payments:', err))
                .finally(() => setIsLoading(false));
        }
    }, [API_URL]);

    return (
        <div className="dashboard-page-new">
            <div className="welcome-section">
                <div>
                    <h1>Payments 💳</h1>
                    <p className="welcome-subtitle">Track your EMI payments and payment history</p>
                </div>
                <button
                    className="btn-primary"
                    onClick={() => navigate('/dashboard/make-payment')}
                    style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        padding: '0.75rem 1.5rem',
                        borderRadius: '10px',
                        border: 'none',
                        fontWeight: '600',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
                    onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
                >
                    💳 Make Payment
                </button>
            </div>

            {/* Upcoming Payments */}
            <div className="content-section">
                <div className="section-header-new">
                    <h2>Upcoming Payments</h2>
                    <span className="loan-count">{upcomingPayments.length} due</span>
                </div>

                {isLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>Loading upcoming payments...</p>
                    </div>
                ) : upcomingPayments.length > 0 ? (
                    <div className="loans-grid">
                        {upcomingPayments.map((payment) => (
                            <div key={payment.id} className="loan-card-new" style={{ borderLeft: '4px solid #f7bd20' }}>
                                <div className="loan-card-header">
                                    <div>
                                        <h3>{payment.loanType}</h3>
                                        <span className="account-number">{payment.accountNumber}</span>
                                    </div>
                                    <span className="stat-badge" style={{ background: '#fff3cd', color: '#856404' }}>
                                        Due in {payment.daysLeft} days
                                    </span>
                                </div>

                                <div className="loan-amount-display">
                                    <span className="amount-label">EMI Amount</span>
                                    <span className="amount-value">₹{payment.amount}</span>
                                </div>

                                <div className="loan-info-grid">
                                    <div className="info-item">
                                        <span className="info-label">Due Date</span>
                                        <span className="info-value">{payment.dueDate}</span>
                                    </div>
                                    <div className="info-item">
                                        <span className="info-label">Status</span>
                                        <span className="info-value" style={{
                                            color: payment.status === 'Active' ? '#10b981' :
                                                payment.status === 'Approved' ? '#3b82f6' : '#e67e22'
                                        }}>
                                            {payment.status}
                                        </span>
                                    </div>
                                </div>

                                <button
                                    className="btn-pay-emi"
                                    onClick={() => navigate('/dashboard/make-payment', { state: { loanId: payment.id.toString() } })}
                                >
                                    Pay EMI Now
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                        <p>No upcoming payments due. You're all caught up!</p>
                    </div>
                )}
            </div>

            {/* Payment History */}
            < div className="content-section" style={{ marginTop: '2rem' }}>
                <div className="section-header-new">
                    <h2>Payment History</h2>
                    <span className="history-count">{paymentHistory.length} transactions</span>
                </div>

                {
                    paymentHistory.length > 0 ? (
                        <div className="disbursement-table">
                            <div className="table-header">
                                <div className="table-col">Date</div>
                                <div className="table-col">Loan Type</div>
                                <div className="table-col">Account Number</div>
                                <div className="table-col">Amount Paid</div>
                                <div className="table-col">Method</div>
                                <div className="table-col">Status</div>
                            </div>
                            {paymentHistory.map((payment) => (
                                <div key={payment.id} className="table-row">
                                    <div className="table-cell">
                                        <span className="cell-label">Date</span>
                                        <span className="cell-value">{payment.date}</span>
                                    </div>
                                    <div className="table-cell">
                                        <span className="cell-label">Loan Type</span>
                                        <span className="cell-value">{payment.loanType}</span>
                                    </div>
                                    <div className="table-cell">
                                        <span className="cell-label">Account</span>
                                        <span className="cell-value account-num">{payment.accountNumber}</span>
                                    </div>
                                    <div className="table-cell">
                                        <span className="cell-label">Amount</span>
                                        <span className="cell-value amount-disbursed">₹{payment.amount}</span>
                                    </div>
                                    <div className="table-cell">
                                        <span className="cell-label">Method</span>
                                        <span className="cell-value">{payment.method}</span>
                                    </div>
                                    <div className="table-cell">
                                        <span className="cell-label">Status</span>
                                        <span className="status-pill completed">{payment.status}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '2rem' }}>
                            <p>No payment history found.</p>
                        </div>
                    )
                }

                <div className="total-disbursed">
                    <span>Total Amount Paid (Last 3 Months)</span>
                    <strong>₹{paymentHistory.reduce((sum, p) => sum + parseInt(p.amount.replace(',', '')), 0).toLocaleString('en-IN')}</strong>
                </div>
            </div >
        </div >
    );
}

export default PaymentsPage;
